import React, { useCallback, useEffect, useId, useState } from "react";
import { useParams } from "react-router-dom";
import { MdCheckCircle, MdEventAvailable } from "react-icons/md";
import { getApiErrorMessage } from "api";
import Button from "components/button";
import Card from "components/card";
import { Textarea } from "components/fields";
import Loader from "components/loader/Loader";
import { EmptyState, ErrorState } from "components/states";
import notify from "lib/notify";
import { formatDate } from "utils/format";
import ChoiceOption from "views/public/shared/ChoiceOption";
import PublicSurveyShell from "views/public/shared/PublicSurveyShell";
import RatingScale from "views/public/shared/RatingScale";
import { isRetryableError } from "views/public/shared/errors";
import teamService from "services/teamService";

const YES_NO = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const isAnswered = (answer) => answer !== undefined && answer !== "";

const CheckInQuestion = ({ question, answer, onAnswer }) => {
  const promptId = useId();

  if (!["scale", "yes_no", "select"].includes(question.questionType)) {
    return (
      <Textarea
        label={question.prompt}
        rows={3}
        value={answer || ""}
        onChange={(e) => onAnswer(e.target.value)}
        placeholder="Type your answer…"
      />
    );
  }

  return (
    <div>
      <p id={promptId} className="mb-2.5 text-sm font-semibold text-fg">
        {question.prompt}
      </p>
      {question.questionType === "scale" && (
        <RatingScale value={answer ?? null} onChange={onAnswer} labelledBy={promptId} />
      )}
      {question.questionType === "yes_no" && (
        <div role="group" aria-labelledby={promptId} className="grid grid-cols-2 gap-2">
          {YES_NO.map(({ value, label }) => (
            <ChoiceOption key={label} selected={answer === value} onClick={() => onAnswer(value)}>
              {label}
            </ChoiceOption>
          ))}
        </div>
      )}
      {question.questionType === "select" && (
        <div role="group" aria-labelledby={promptId} className="flex flex-col gap-2">
          {(question.options || []).map((option) => (
            <ChoiceOption key={option} selected={answer === option} onClick={() => onAnswer(option)}>
              {option}
            </ChoiceOption>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Public, token-based weekly check-in with no login. Links arrive by email/SMS,
 * so the page is mobile-first.
 */
const TeamCheckInPage = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const loadCheckIn = useCallback(async () => {
    if (!token) return;
    setError(null);
    setLoading(true);
    try {
      const res = await teamService.getPublicCheckIn(token);
      const d = res.data;
      setData(d);
      if (d?.submission) {
        const prefill = {};
        (d.submission.answers || []).forEach((a) => {
          prefill[a.questionId] = a.answer;
        });
        setAnswers(prefill);
      }
    } catch (err) {
      setError({
        message: err?.response?.data?.error || "This check-in link is invalid or has expired.",
        retryable: isRetryableError(err),
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadCheckIn();
  }, [loadCheckIn]);

  const setAnswer = (questionId, value) => setAnswers((a) => ({ ...a, [questionId]: value }));

  const questions = data?.questions || [];
  const answeredCount = questions.filter((q) => isAnswered(answers[q.id])).length;

  const handleSubmit = async () => {
    const payload = questions
      .filter((q) => isAnswered(answers[q.id]))
      .map((q) => ({ questionId: q.id, answer: answers[q.id] }));
    if (!payload.length) return;

    try {
      setSubmitting(true);
      await teamService.submitPublicCheckIn(token, payload);
      setDone(true);
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Couldn't submit your check-in. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const grouped = questions.reduce((acc, q) => {
    const key = q.leader ? `${q.leader.firstName} ${q.leader.lastName}` : "Your leadership";
    if (!acc[key]) acc[key] = [];
    acc[key].push(q);
    return acc;
  }, {});

  if (loading) {
    return (
      <PublicSurveyShell center>
        <Loader size="lg" label="Loading your check-in" />
      </PublicSurveyShell>
    );
  }

  if (error) {
    return (
      <PublicSurveyShell center>
        <ErrorState
          title="Check-In Unavailable"
          titleAs="h1"
          message={
            <>
              {error.message}
              <span className="mt-1 block">Ask your leader to send you a fresh check-in link.</span>
            </>
          }
          onRetry={error.retryable ? loadCheckIn : undefined}
        />
      </PublicSurveyShell>
    );
  }

  if (done) {
    return (
      <PublicSurveyShell center>
        <div className="text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-subtle text-success-fg">
            <MdCheckCircle aria-hidden="true" className="h-8 w-8" />
          </span>
          <h1 className="mt-4 text-title">You're Checked In</h1>
          <p className="mt-2 text-sm text-fg-muted">
            Thanks{data?.firstName ? `, ${data.firstName}` : ""}. Your leaders will see your update.
            Have a great week.
          </p>
        </div>
      </PublicSurveyShell>
    );
  }

  if (!questions.length) {
    return (
      <PublicSurveyShell center>
        <EmptyState
          icon={MdEventAvailable}
          title="Nothing to Answer This Week"
          titleAs="h1"
          description="Your leaders haven't asked any questions yet. Check back next week."
        />
      </PublicSurveyShell>
    );
  }

  return (
    <PublicSurveyShell panel={false}>
      <div className="mb-6 text-center">
        <h1 className="text-title">
          {data?.firstName ? `Hey ${data.firstName}` : "Weekly Check-In"}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">
          Weekly check-in · Week of {formatDate(data?.weekStart)}
          {data?.submission ? " · updating your earlier answers" : ""}
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {Object.entries(grouped).map(([leaderName, leaderQuestions]) => (
          <Card key={leaderName} as="section" padding="md" aria-label={`${leaderName} asks`}>
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-fg-muted">
              {leaderName} asks
            </h2>
            <div className="flex flex-col gap-6">
              {leaderQuestions.map((q) => (
                <CheckInQuestion
                  key={q.id}
                  question={q}
                  answer={answers[q.id]}
                  onAnswer={(value) => setAnswer(q.id, value)}
                />
              ))}
            </div>
          </Card>
        ))}
      </div>

      <Button
        size="lg"
        fullWidth
        className="mt-6"
        onClick={handleSubmit}
        disabled={answeredCount === 0}
        loading={submitting}
      >
        {submitting
          ? "Submitting…"
          : data?.submission
          ? "Update My Check-In"
          : `Submit Check-In${answeredCount ? ` (${answeredCount}/${questions.length})` : ""}`}
      </Button>
    </PublicSurveyShell>
  );
};

export default TeamCheckInPage;
